import { zodResolver } from '@hookform/resolvers/zod';
import HelpIcon from '@mui/icons-material/Help';
import {
  Box,
  Button,
  FormLabel,
  Grid,
  Skeleton,
  TextField,
  Tooltip,
} from '@mui/material';
import { DateTime } from 'luxon';
import { Controller, useForm } from 'react-hook-form';
import { UserAvatarEdit } from '..';
import { userInfoValidationSchema, type UserInfoFormData } from './schema';
import { DatePicker } from '@/components/shared/date-picker';
import { MaskedInput } from '@/components/shared/masked-input';
import { useNotification } from '@/context/notification';
import { useProfileShow, useProfileUpdate } from '@/data/account/profile';
import { useScreenSizes } from '@/hooks/use-screen-sizes';
import { formatDate } from '@/utils/format-date';

type UserInfoFormProps = {
  isUpdating?: boolean;
  userAvatar?: string;
  onSubmit: (data: UserInfoFormData) => void;
  defaultValues?: {
    name?: string;
    phone?: string;
    birthdate?: string;
    email?: string;
    personal_email?: string;
  };
};

export const Content = ({
  onSubmit,
  defaultValues,
  userAvatar,
  isUpdating,
}: UserInfoFormProps) => {
  const { control, handleSubmit } = useForm({
    defaultValues,
    resolver: zodResolver(userInfoValidationSchema),
  });

  const { isSmallScreen } = useScreenSizes();

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid
        size={{ xs: 12, md: 2 }}
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
        }}
        mb={4}
      >
        <UserAvatarEdit
          name={defaultValues?.name || ''}
          email={defaultValues?.email || ''}
          imageSource={userAvatar}
        />
        {!isSmallScreen && (
          <Button
            type="submit"
            variant="contained"
            sx={{ width: { xs: '100%', md: 'fit-content' } }}
            loading={isUpdating}
          >
            Atualizar Perfil
          </Button>
        )}
      </Grid>
      <Grid
        container
        rowSpacing={{ xs: 3, md: 1 }}
        columnSpacing={3}
        sx={{ display: 'flex' }}
      >
        <Grid
          container
          rowSpacing={{ xs: 1, md: 2 }}
          columnSpacing={3}
          size={12}
        >
          <Grid size={{ xs: 12, md: 4 }}>
            <FormLabel id="birthdate-label" htmlFor="birthdate">
              Data de nascimento
            </FormLabel>
            <Controller
              name="birthdate"
              control={control}
              render={({ field, fieldState: { error } }) => {
                const dateValue = field.value
                  ? DateTime.fromFormat(field.value, 'dd/MM/yyyy')
                  : null;

                return (
                  <DatePicker
                    field={field}
                    value={dateValue}
                    slotProps={{
                      textField: {
                        id: 'birthdate',
                        error: !!error,
                        helperText: error?.message || ' ',
                        'aria-labelledby': 'birthdate-label',
                      },
                    }}
                  />
                );
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormLabel id="phone-label" htmlFor="phone">
              Número de celular
            </FormLabel>
            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <MaskedInput
                  {...field}
                  id="phone"
                  onAccept={field.onChange}
                  fullWidth
                  error={!!error}
                  helperText={error?.message || ' '}
                  mask={[
                    { mask: '(00) 0000-0000' },
                    { mask: '(00) 00000-0000' },
                  ]}
                  placeholder="(00) 0000-0000"
                  aria-labelledby="phone-label"
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormLabel id="personal_email-label" htmlFor="personal_email">
              Email para comunicação
              <Tooltip
                title="Este é o email que você receberá comunicações de ofertas."
                placement="top"
                arrow
              >
                <HelpIcon
                  fontSize="small"
                  sx={{ ml: 1, color: 'neutral.500' }}
                />
              </Tooltip>
            </FormLabel>
            <Controller
              name="personal_email"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="personal_email"
                  fullWidth
                  error={!!error}
                  helperText={error?.message || ' '}
                  placeholder="nome@email.com"
                  aria-labelledby="personal_email-label"
                />
              )}
            />
          </Grid>
        </Grid>

        {isSmallScreen && (
          <Grid
            size={{ xs: 12 }}
            sx={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button
              type="submit"
              variant="contained"
              sx={{ width: { xs: '100%', md: 'fit-content' } }}
              loading={isUpdating}
            >
              Atualizar Perfil
            </Button>
          </Grid>
        )}
      </Grid>
    </form>
  );
};

export const UserInfoForm = () => {
  const { toast } = useNotification();
  const { profile, isLoading } = useProfileShow();
  const { mutate: profileUpdate, isPending } = useProfileUpdate();

  const onSubmit = (data: UserInfoFormData) => {
    profileUpdate(
      {
        name: data.name || '',
        phone: data.phone || '',
        birthdate: data.birthdate || '',
        personal_email: data.personal_email || '',
      },
      {
        onSuccess: () => {
          toast({
            content: 'Perfil atualizado',
            type: 'success',
          });
        },
        onError: () => {
          toast({
            content: 'Erro ao atualizar perfil',
            type: 'error',
          });
        },
      }
    );
  };

  return (
    <Box>
      {isLoading ? (
        <Skeleton variant="rounded" height={200} />
      ) : (
        <Content
          onSubmit={onSubmit}
          userAvatar={profile?.avatar}
          isUpdating={isPending}
          defaultValues={{
            name: profile?.name || '',
            phone: profile?.cellphone_number || '',
            birthdate: profile?.birth_date ? formatDate(profile.birth_date) : '',
            personal_email: profile?.personal_email || '',
            email: profile?.email || '',
          }}
        />
      )}
    </Box>
  );
};
